import Transaction, { FutureTransactionMeta } from '@/models/Transaction';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import Debit, { DEBIT_METHOD_BANK } from '@/models/Debit';
import { CommissionRules } from '@/models/CommissionRules';
import { isUnlimited } from '@/pages/Donation/StandingOrders/standingOrderModel';
import SupportRequestType from '@/models/common/SupportRequestType';
import Balance from '@/models/Balance';
import { parseTransactionDate } from '@/helpers/dateHelper';
import { HDate, months } from '@hebcal/hdate';

/*
 * HAT-1203 — «תנועות עתידיות» on the transactions board.
 *
 * The board shows money that has not moved yet in a group of its own, above the
 * settled movements. What goes into that group was decided by Druk (9.9.2026):
 *
 *  - movement details the accounting system already holds with a value date ahead of
 *    today (credit-card instalments, post-dated checks, refunds) — the next 7 days;
 *  - months of approved, unpaid support requests — monthly ones up to the horizon's edge
 *    (an approved month that is overdue is still money about to leave), one-time ones
 *    always, whatever their date;
 *  - bank standing-order charges, which the accounting system records only after the
 *    bank collects them, so the next one is *projected* from the order's billing day and
 *    captioned as such (Druk, 15.9.2026: from the accounting system, not from Nedarim).
 *    One row per collection date, not per order (HAT-1309): a fund can hold a hundred and
 *    fifty live orders on six billing days, and one row each would bury the supports.
 *
 * Everything here is pure so it can be tested without a table.
 */

/**
 * How far ahead the group looks. Druk's decision (9.9.2026, question 2): a week, fixed — not
 * the board's date filter, and not a picker of its own. A coordinator narrowing last month
 * must not lose this week's charges, and a picker was tried and withdrawn (15.9.2026).
 */
export const FUTURE_WINDOW_DAYS = 7;
/** Must match FeatureFlagKeys.FutureTransactions on the server and the row seeded by SeedFeatureFlag_FutureTransactions.sql. */
export const FUTURE_TRANSACTIONS_FEATURE_KEY = 'future-transactions';
/**
 * The bank standing-order rows inside the group have a switch of their own (HAT-1309): the
 * group itself is already open to everyone, and these rows are a projection the office wants
 * to look at on real funds first. Must match FeatureFlagKeys.FutureBankStandingOrders and
 * SeedFeatureFlag_FutureBankStandingOrders.sql.
 */
export const FUTURE_BANK_STANDING_ORDERS_FEATURE_KEY = 'future-bank-standing-orders';
export const FUTURE_GROUP_ID = 'future';
export const CURRENT_GROUP_ID = 'current';

/**
 * Sub-request statuses that mean "approved and not yet paid". A month that reached
 * Done is already a real movement (counting it again would double it); Rejected and
 * Cancelled are not commitments; PendingSignatures and the committee queue are not
 * approved yet.
 */
export const APPROVED_UNPAID_STATUSES: ReadonlySet<string> = new Set(['InitialApprovalApproved', 'PendingExecution']);

/**
 * How far ahead the server's available balance already holds approved support months back
 * (NewAptFundsFinancialDataService.FutureTimeForPendingBalance). The coverage check must know
 * which of the rows it shows were deducted before it compares — see flagInsufficientCoverage.
 */
export const PENDING_BALANCE_HOLDBACK_DAYS = 5;

export interface FutureWindow {
  /** The last instant that still counts as "now" — the end of today. */
  today: Date;
  /** The last instant the group looks ahead to. */
  end: Date;
}

const endOfDay = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
};

const addDays = (date: Date, days: number): Date => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

export const futureWindow = (now: Date = new Date(), days: number = FUTURE_WINDOW_DAYS): FutureWindow => ({
  today: endOfDay(now),
  end: endOfDay(addDays(now, days)),
});

export const isFutureRow = (row: Transaction): boolean => Boolean(row.future);
export const isCurrentRow = (row: Transaction): boolean => !row.future;
/** A row the board made up from a support month or a standing order — no movement, no ids, no commission. */
export const isSyntheticRow = (row: Transaction): boolean => Boolean(row.future && row.future.source !== 'apt');

/**
 * What the status column, the status filter and the search say about a row. `projected` is a
 * standing-order charge computed from the order rather than recorded. A month whose date has
 * passed is a plain future row: HAT-1311 took «באיחור» off the board.
 */
export type TransactionStatus = 'current' | 'future' | 'projected';
export const TRANSACTION_STATUSES: readonly TransactionStatus[] = ['current', 'future', 'projected'];
export const transactionStatus = (row: Transaction): TransactionStatus => {
  if (!row.future) return 'current';
  if (row.future.projected) return 'projected';
  return 'future';
};

/**
 * Splits what the transactions API returned by value date. Anything dated after today
 * is a future movement (source `apt`); a row whose date cannot be read stays current,
 * where the board already knows how to show it (HAT-1201).
 */
export const splitAptTransactions = (rows: Transaction[], window: FutureWindow): { current: Transaction[]; future: Transaction[] } => {
  const current: Transaction[] = [];
  const future: Transaction[] = [];
  for (const row of rows) {
    const date = parseTransactionDate(row.date);
    if (date && date > window.today) {
      future.push({ ...row, future: { source: 'apt' } });
    } else {
      current.push(row);
    }
  }
  return { current, future };
};

/* The fields a synthetic row must carry so every cell and export column can read it. */
const blankRow = (fundId: number): Omit<Transaction, 'date' | 'kind' | 'contactName' | 'amount' | 'paymentMethod' | 'future'> => ({
  contactId: 0,
  contactIsraeliId: '',
  cumulativeAmount: 0,
  ilsAmount: undefined,
  exchangeRate: undefined,
  fundId,
  receiptId: 0,
  donationReference: null,
  commissionFixed: 0,
  commissionPercentage: 0,
  aptNotes: '',
  notes: '',
  transactionId: 0,
  transactionDetailId: 0,
  monthlyCommission: null,
  currency: 'ILS',
  commissionAmount: 0,
  commissionType: '',
  additionalCommissionAmount: 0,
  additionalCommissionType: '',
  defaultReceipt: true,
});

const supportedName = (request: SupportRequestDtoIn): string => {
  const contact = request.supportedContact;
  if (contact) return [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  return request.supplier?.name ?? '';
};

/**
 * One row per approved, unpaid month of a support request. The API returns a request
 * as one object with parallel `requestedForDates` / `statuses` / `approvedAmounts` arrays;
 * a request still in initial review carries a single status for all its months and is
 * skipped.
 *
 * Monthly supports contribute their months up to the horizon's edge — including a month
 * whose date has passed and is still awaiting execution, because that money leaves the
 * moment the office catches up. A one-time support that was approved and not paid is a
 * commitment whatever its date, so it is always in.
 *
 * Each month carries its own approved figure: the request's `amount` is the *requested*
 * one for a monthly request, and the office approves month by month. Amounts are
 * negative: this is money leaving the fund.
 */
export const supportRequestsToFutureRows = (requests: SupportRequestDtoIn[], fundId: number, window: FutureWindow): Transaction[] => {
  const rows: Transaction[] = [];
  for (const request of requests) {
    const dates = request.requestedForDates ?? [];
    const statuses = request.statuses ?? [];
    if (dates.length === 0 || statuses.length !== dates.length) continue;

    const isMonthly = request.type === SupportRequestType.Monthly;
    dates.forEach((requestedFor, index) => {
      if (!APPROVED_UNPAID_STATUSES.has(statuses[index])) return;
      const date = parseTransactionDate(requestedFor);
      if (!date) return;
      if (isMonthly && date > window.end) return;
      const amount = request.approvedAmounts?.[index] ?? request.amount;

      rows.push({
        ...blankRow(fundId),
        date,
        kind: 'תמיכה',
        contactName: supportedName(request),
        contactId: request.supportedContact?.id ?? 0,
        contactIsraeliId: request.supportedContact?.israeliId ?? '',
        amount: -Math.abs(amount),
        paymentMethod: 'bankTransfer',
        transactionDetailId: index,
        future: { source: 'support', correlationId: request.correlationId },
      });
    });
  }
  return rows;
};

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
/** The given day of a month, or its last day when the month is shorter (a 31st order charges 30 April). */
const dayOfMonth = (year: number, month: number, day: number): Date => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
};
const sameMonth = (a: Date, b: Date): boolean => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/**
 * When a bank standing order is next collected, or null when it will not be: it is inactive, a
 * credit-card order (those instalments already reach the board as accounting rows), out of
 * payments, or past its end. Monthly on the order's billing day; the charge already collected
 * this month (the latest detail's value date) pushes the projection to next month.
 */
export const nextStandingOrderCharge = (debit: Debit, now: Date = new Date()): Date | null => {
  if (!debit.isActive || debit.paymentMethod !== DEBIT_METHOD_BANK) return null;
  const day = debit.billingDayOfMonth;
  if (!day || day < 1 || day > 31) return null;
  // 999 payments is the accounting system's way of saying "no limit" (standingOrderModel.UNLIMITED_PAYMENTS).
  if (!isUnlimited(debit.numberOfPayments) && debit.numberOfPayments > 0 && debit.countCharged >= debit.numberOfPayments) return null;

  const today = startOfDay(now);
  let candidate = dayOfMonth(today.getFullYear(), today.getMonth(), day);
  const lastCharge = parseTransactionDate(debit.lastChargeAt);
  if (candidate <= today || (lastCharge && sameMonth(lastCharge, candidate))) {
    candidate = dayOfMonth(today.getFullYear(), today.getMonth() + 1, day);
  }
  const endsAt = parseTransactionDate(debit.endsAt);
  if (endsAt && candidate > endsAt) return null;
  return candidate;
};

/*
 * The days Israeli banks do not collect: Shabbat and the festival days (יום טוב) - the first
 * and second of Rosh Hashana, Yom Kippur, the first day of Sukkot, Shmini Atzeret, the first
 * and seventh of Pesach, Shavuot - and Yom Ha'atzmaut. Chol hamoed and Friday are working days.
 */
const YOM_TOV: ReadonlyArray<readonly [number, number]> = [
  [months.TISHREI, 1],
  [months.TISHREI, 2],
  [months.TISHREI, 10],
  [months.TISHREI, 15],
  [months.TISHREI, 22],
  [months.NISAN, 15],
  [months.NISAN, 21],
  [months.SIVAN, 6],
];

/** Yom Ha'atzmaut is 5 Iyyar, moved off Shabbat's edges: Friday/Shabbat back to Thursday, Monday on to Tuesday. */
const isYomHaatzmaut = (hebrew: HDate): boolean => {
  if (hebrew.getMonth() !== months.IYYAR) return false;
  const fifth = new HDate(5, months.IYYAR, hebrew.getFullYear());
  const weekday = fifth.getDay();
  const observed = weekday === 5 ? 4 : weekday === 6 ? 3 : weekday === 1 ? 6 : 5;
  return hebrew.getDate() === observed;
};

/** True on a day the bank does not collect a standing order. */
export const isBankHoliday = (date: Date): boolean => {
  if (date.getDay() === 6) return true;
  const hebrew = new HDate(date);
  if (YOM_TOV.some(([month, day]) => hebrew.getMonth() === month && hebrew.getDate() === day)) return true;
  return isYomHaatzmaut(hebrew);
};

/** The day itself, or the next day the bank collects (Druk, 23.9.2026). */
export const nextBankBusinessDay = (date: Date): Date => {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  while (isBankHoliday(day)) day.setDate(day.getDate() + 1);
  return day;
};

/** The calendar day a projected charge falls on, as the row id and the grouping key use it. */
const dateKey = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * The bank standing orders due inside the window, one incoming row per collection date: «67
 * הוראות קבע בנקאיות», the sum of their monthly amounts, captioned as a projection (Druk,
 * 9.9.2026, question 3). The orders come from the accounting system's own list (`/debits`), not
 * from Nedarim, whose bank feed was switched off on 10.9.2026.
 *
 * Why one row per date and not per order (HAT-1309, 17.9.2026): on the real data a fund had 151
 * live orders on six billing days, and the group exists to show the coordinator what is about to
 * leave. The projection itself was checked against 3,000 collected charges: the cycle is always
 * monthly, the value date is the billing day in 97% of them and the day after in the rest, and
 * the rules in nextStandingOrderCharge kept exactly the orders that were still collecting.
 *
 * A billing day that falls on Shabbat or a festival is projected on the next business day
 * (Druk, 23.9.2026), so the orders of a Shabbat and the Sunday after share one row. The row
 * carries every order it stands for: a click opens the whole list (Druk: all of it, not part).
 *
 * `label` names the row for its count, in the board's language; the rest of the row is data.
 */
export const bankStandingOrdersToFutureRows = (
  debits: Debit[],
  fundId: number,
  window: FutureWindow,
  label: (count: number) => string,
  now: Date = new Date()
): Transaction[] => {
  const byDate = new Map<string, { date: Date; amount: number; orders: Debit[] }>();
  for (const debit of debits) {
    const due = nextStandingOrderCharge(debit, now);
    if (!due) continue;
    const next = nextBankBusinessDay(due);
    if (next <= window.today || next > window.end) continue;
    const key = dateKey(next);
    const group = byDate.get(key) ?? { date: next, amount: 0, orders: [] };
    group.amount += Math.abs(debit.monthlyAmount);
    group.orders.push(debit);
    byDate.set(key, group);
  }
  return [...byDate.values()].map((group) => ({
    ...blankRow(fundId),
    date: group.date,
    kind: 'תרומה',
    contactName: label(group.orders.length),
    amount: group.amount,
    paymentMethod: 'directDebit',
    future: { source: 'standingOrder', projected: true, orderCount: group.orders.length, standingOrders: group.orders },
  }));
};

const magnitude = (row: Transaction): number => Math.abs(row.ilsAmount ?? row.amount);
const roundUp = (value: number): number => Math.ceil(value * 100) / 100;

/** What the future group costs against what the fund can pay — the figures the alert prints. */
export interface FutureCoverage {
  /** The outgoing rows themselves. */
  outgoing: number;
  /** The commissions those rows will carry once they settle. */
  fees: number;
  /** `outgoing + fees` — «בסך». */
  total: number;
  /** The available balance, with the supports it already holds back credited back. */
  coverage: number;
  /** `total − coverage`, never below zero. */
  shortfall: number;
  /** What a coordinator has to deposit by bank transfer to close the shortfall, its own commission included — «חסר». */
  deposit: number;
  /** The bank-transfer rate the deposit was grossed up by, for the caption. */
  depositPercent: number;
}

/**
 * The cumulative check Druk asked for — the available balance against everything about to
 * leave — with the commissions folded in (Druk, 15.9.2026): each support month costs the
 * fund a flat fee, an accounting row already carries its computed commission, and the
 * deposit that closes the gap is itself charged a percentage. Without them the coordinator
 * deposits the "missing 500" and is then short another 13.
 *
 * The available balance already holds back the approved support months due within the next
 * few days, and those same months are rows here; only *their* amounts are credited back
 * before comparing — not the balance's whole `pendingExecutionSum`, which also covers
 * requests still in review and months this group does not show — and never more than the
 * balance actually deducted, since the two figures come from two calls.
 */
export const coverageShortfall = (rows: Transaction[], balance: Balance | null, window: FutureWindow, rules: CommissionRules | null): FutureCoverage | null => {
  if (!balance) return null;
  const outgoingRows = rows.filter((row) => row.future && row.amount < 0);
  if (outgoingRows.length === 0) return null;

  const outgoing = outgoingRows.reduce((sum, row) => sum + magnitude(row), 0);
  const chargesFees = Boolean(rules?.enabled);
  const fees = outgoingRows.reduce((sum, row) => {
    if (row.future?.source === 'support') return sum + (chargesFees ? (rules?.supportRowFee ?? 0) : 0);
    return sum + Math.abs(row.commissionAmount || 0) + Math.abs(row.additionalCommissionAmount || 0);
  }, 0);
  const total = outgoing + fees;

  // HAT-1396: the balance holds back each of those months with its support-row fee, so the fee is credited back too.
  const heldBackFee = balance.supportRequestsBalances?.supportRowFee ?? 0;
  const holdbackEdge = addDays(window.today, PENDING_BALANCE_HOLDBACK_DAYS);
  const shownAndHeldBack = outgoingRows
    .filter((row) => row.future?.source === 'support' && (parseTransactionDate(row.date) ?? holdbackEdge) <= holdbackEdge)
    .reduce((sum, row) => sum + magnitude(row) + heldBackFee, 0);
  const alreadyHeldBack = Math.min(shownAndHeldBack, balance.supportRequestsBalances?.pendingExecutionSum ?? 0);
  const coverage = balance.calculateAvailableBalance() + alreadyHeldBack;

  const shortfall = Math.max(0, total - coverage);
  const bank = chargesFees ? rules!.bankTransfer : { fixed: 0, percent: 0 };
  const depositPercent = bank.percent > 0 && bank.percent < 100 ? bank.percent : 0;
  const deposit = shortfall > 0 ? roundUp((shortfall + (chargesFees ? bank.fixed : 0)) / (1 - depositPercent / 100)) : 0;

  return { outgoing, fees, total, coverage, shortfall, deposit, depositPercent };
};

/** Marks every outgoing future row when the group as a whole is not covered. */
export const flagInsufficientCoverage = (rows: Transaction[], coverage: FutureCoverage | null): Transaction[] => {
  if (!coverage || coverage.shortfall <= 0) return rows;
  return rows.map((row) => (row.future && row.amount < 0 ? { ...row, future: { ...row.future, alert: true } } : row));
};

/**
 * One row per movement, whichever call brought it. The board's own date range can reach past
 * today (a custom range typed by hand), and then the main call returns the same post-dated
 * details the future call does; the first copy wins.
 */
export const dedupeFutureRows = (rows: Transaction[]): Transaction[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const id = futureRowId(row);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/** Soonest first — the group answers "what happens next". */
export const sortSoonestFirst = (rows: Transaction[]): Transaction[] =>
  [...rows].sort((a, b) => (parseTransactionDate(a.date)?.getTime() ?? 0) - (parseTransactionDate(b.date)?.getTime() ?? 0));

/**
 * A stable row id that cannot collide across the sources. A bank standing-order row stands for
 * every order collected on one date, so the date is its identity.
 *
 * A settled row is keyed by movement *and* detail: an instalment plan is one movement
 * with a detail per charge, so `transactionId` alone repeats — the board on master keyed
 * rows by it and React warned about duplicate keys on every such fund. The same
 * movement can now also have a detail in each group (this month settled, next month
 * ahead), which the `apt-` prefix keeps apart.
 */
export const futureRowId = (row: Transaction): string => {
  const meta: FutureTransactionMeta | undefined = row.future;
  if (!meta) return `${row.transactionId}-${row.transactionDetailId}`;
  if (meta.source === 'support') return `support-${meta.correlationId}-${row.transactionDetailId}`;
  if (meta.source === 'standingOrder') {
    const date = parseTransactionDate(row.date);
    return `standing-order-${date ? dateKey(date) : row.transactionDetailId}`;
  }
  return `apt-${row.transactionId}-${row.transactionDetailId}`;
};
